import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, LogOut, Building2, Lock, Ban, CheckCircle2, RefreshCw, CreditCard, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "cotflow_admin_token";
const PLAN_PRICE = { pro: 9990, empresa: 14990 };

const PLAN_STYLES = {
  free: "bg-slate-100 text-slate-600 border-slate-200",
  pro: "bg-blue-50 text-blue-700 border-blue-200",
  empresa: "bg-violet-50 text-violet-700 border-violet-200",
};

const PAY_STATUS = {
  al_dia: { label: "Al día", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" },
  por_vencer: { label: "Por vencer", cls: "bg-amber-500/10 text-amber-400 border-amber-500/30" },
  vencido: { label: "Vencido", cls: "bg-red-500/10 text-red-400 border-red-500/30" },
  solicitado: { label: "Solicitó plan", cls: "bg-blue-500/10 text-blue-400 border-blue-500/30" },
  sin_registro: { label: "Sin pago reg.", cls: "bg-slate-500/10 text-slate-400 border-slate-500/30" },
  free: { label: "Free", cls: "bg-slate-500/10 text-slate-400 border-slate-500/30" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-CL", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "—";
  }
};
const fmtCLP = (n) => `$${Number(n).toLocaleString("es-CL")}`;

function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/backoffice/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      onLogin(res.data.token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Credenciales incorrectas");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <form onSubmit={submit} className="w-full max-w-sm bg-slate-900 border border-slate-800 rounded-2xl p-8 space-y-5" data-testid="backoffice-login-form">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-white">Backoffice Cotflow</h1>
            <p className="text-xs text-slate-400">Acceso restringido</p>
          </div>
        </div>
        <Input data-testid="backoffice-email-input" type="email" placeholder="Email de administrador" value={email}
          onChange={(e) => setEmail(e.target.value)} required
          className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500" />
        <Input data-testid="backoffice-password-input" type="password" placeholder="Contraseña" value={password}
          onChange={(e) => setPassword(e.target.value)} required
          className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500" />
        <Button data-testid="backoffice-login-submit" type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-500 text-white">
          <Lock className="w-4 h-4 mr-2" />
          {loading ? "Verificando..." : "Ingresar"}
        </Button>
      </form>
    </div>
  );
}

function PaymentModal({ company, token, onClose, onDone }) {
  const plan = company.requested_plan || company.plan;
  const [months, setMonths] = useState("1");
  const [amount, setAmount] = useState(String((PLAN_PRICE[plan] || 0) * 1));
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const changeMonths = (m) => {
    setMonths(m);
    setAmount(String((PLAN_PRICE[plan] || 0) * Number(m)));
  };

  const submit = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/backoffice/companies/${company.id}/payments`,
        { months: Number(months), amount: Number(amount), note },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrar el pago");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="payment-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Registrar pago</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="payment-modal-close"><X size={18} /></button>
        </div>
        <p className="text-sm text-slate-400 mb-1">{company.name} · {company.rut || "Sin RUT"}</p>
        <p className="text-sm text-slate-300 mb-4">
          Plan a activar: <span className="font-semibold text-white capitalize">{plan}</span>
          {company.requested_plan && <span className="ml-2 text-xs text-blue-400">(solicitado por el cliente)</span>}
        </p>
        <div className="space-y-4">
          <div>
            <label className="text-xs text-slate-400 block mb-1.5">Meses pagados</label>
            <Select value={months} onValueChange={changeMonths}>
              <SelectTrigger data-testid="payment-months-select" className="bg-slate-800 border-slate-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {["1", "3", "6", "12"].map((m) => <SelectItem key={m} value={m}>{m} {m === "1" ? "mes" : "meses"}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1.5">Monto recibido (CLP)</label>
            <Input data-testid="payment-amount-input" type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
              className="bg-slate-800 border-slate-700 text-white" />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1.5">Nota (opcional)</label>
            <Input data-testid="payment-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ej: transferencia 05/06, comprobante #123"
              className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-600" />
          </div>
        </div>
        <Button data-testid="payment-submit-btn" onClick={submit} disabled={saving || !amount} className="w-full mt-5 bg-emerald-600 hover:bg-emerald-500 text-white">
          {saving ? "Registrando..." : "Confirmar pago y activar"}
        </Button>
      </div>
    </div>
  );
}

export default function BackofficePage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [tab, setTab] = useState("empresas");
  const [companies, setCompanies] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [payModal, setPayModal] = useState(null);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setCompanies([]);
    setPayments([]);
  }, []);

  const load = useCallback(async (tok) => {
    setLoading(true);
    try {
      const H = { headers: { Authorization: `Bearer ${tok}` } };
      const [c, p] = await Promise.all([
        axios.get(`${API}/backoffice/companies`, H),
        axios.get(`${API}/backoffice/payments`, H),
      ]);
      setCompanies(c.data);
      setPayments(p.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) logout();
      else toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const toggleStatus = async (c) => {
    try {
      const res = await axios.put(`${API}/backoffice/companies/${c.id}/status`,
        { is_blocked: !c.is_blocked }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      setCompanies((prev) => prev.map((x) => (x.id === c.id ? { ...x, is_blocked: !c.is_blocked } : x)));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar estado");
    }
  };

  const changePlan = async (c, plan) => {
    try {
      const res = await axios.put(`${API}/backoffice/companies/${c.id}/plan`,
        { plan }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      setCompanies((prev) => prev.map((x) => (x.id === c.id ? { ...x, plan } : x)));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cambiar plan");
    }
  };

  if (!token) return <AdminLogin onLogin={setToken} />;

  const total = companies.length;
  const blocked = companies.filter((c) => c.is_blocked).length;
  const paying = companies.filter((c) => c.plan !== "free").length;
  const overdue = payments.filter((p) => p.payment_status === "vencido").length;
  const requests = payments.filter((p) => p.payment_status === "solicitado").length;

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="backoffice-dashboard">
      <header className="border-b border-slate-800 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-base font-semibold">Backoffice Cotflow</h1>
            <p className="text-xs text-slate-400">Administración de empresas</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="backoffice-refresh-btn" variant="ghost" size="sm" onClick={() => load(token)} className="text-slate-300 hover:text-white hover:bg-slate-800">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button data-testid="backoffice-logout-btn" variant="ghost" size="sm" onClick={logout} className="text-slate-300 hover:text-white hover:bg-slate-800">
            <LogOut className="w-4 h-4 mr-1.5" /> Salir
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: "Empresas registradas", value: total, testid: "kpi-total" },
            { label: "Con plan de pago", value: paying, testid: "kpi-paying" },
            { label: "Solicitudes de plan", value: requests, testid: "kpi-requests", accent: requests > 0 ? "text-blue-400" : "" },
            { label: "Pagos vencidos", value: overdue, testid: "kpi-overdue", accent: overdue > 0 ? "text-red-400" : "" },
            { label: "Bloqueadas", value: blocked, testid: "kpi-blocked" },
          ].map((k) => (
            <div key={k.label} data-testid={k.testid} className="bg-slate-900 border border-slate-800 rounded-xl p-4">
              <p className="text-xs text-slate-400">{k.label}</p>
              <p className={`text-2xl font-bold mt-1 ${k.accent || ""}`}>{k.value}</p>
            </div>
          ))}
        </div>

        <div className="flex gap-2">
          <button data-testid="tab-empresas" onClick={() => setTab("empresas")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === "empresas" ? "bg-emerald-600 text-white" : "bg-slate-900 text-slate-400 border border-slate-800 hover:text-white"}`}>
            <Building2 className="w-4 h-4 inline mr-1.5 -mt-0.5" />Empresas
          </button>
          <button data-testid="tab-pagos" onClick={() => setTab("pagos")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === "pagos" ? "bg-emerald-600 text-white" : "bg-slate-900 text-slate-400 border border-slate-800 hover:text-white"}`}>
            <CreditCard className="w-4 h-4 inline mr-1.5 -mt-0.5" />Pagos
            {requests > 0 && <span className="ml-2 bg-blue-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{requests}</span>}
          </button>
        </div>

        {tab === "empresas" && (
          <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="companies-table">
                <thead>
                  <tr className="text-left text-xs text-slate-400 border-b border-slate-800">
                    <th className="px-5 py-3 font-medium">Empresa</th>
                    <th className="px-3 py-3 font-medium">Owner</th>
                    <th className="px-3 py-3 font-medium">Plan</th>
                    <th className="px-3 py-3 font-medium text-center">Usuarios</th>
                    <th className="px-3 py-3 font-medium text-center">Cotizaciones</th>
                    <th className="px-3 py-3 font-medium">Registro</th>
                    <th className="px-3 py-3 font-medium">Último acceso</th>
                    <th className="px-3 py-3 font-medium">Estado</th>
                    <th className="px-5 py-3 font-medium text-right">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map((c) => (
                    <tr key={c.id} data-testid={`company-row-${c.id}`} className="border-b border-slate-800/60 hover:bg-slate-800/30">
                      <td className="px-5 py-3">
                        <p className="font-medium text-white">{c.name || "—"}</p>
                        <p className="text-xs text-slate-500">{c.rut || "Sin RUT"}</p>
                      </td>
                      <td className="px-3 py-3 text-slate-300">{c.owner_email || "—"}</td>
                      <td className="px-3 py-3">
                        <Select value={c.plan} onValueChange={(v) => changePlan(c, v)}>
                          <SelectTrigger data-testid={`plan-select-${c.id}`} className={`h-7 w-28 text-xs border rounded-full px-3 ${PLAN_STYLES[c.plan] || PLAN_STYLES.free}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="free">Free</SelectItem>
                            <SelectItem value="pro">Pro</SelectItem>
                            <SelectItem value="empresa">Empresa</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-3 py-3 text-center text-slate-300">{c.user_count}</td>
                      <td className="px-3 py-3 text-center text-slate-300">{c.quotation_count}</td>
                      <td className="px-3 py-3 text-slate-400 text-xs">{fmtDate(c.created_at)}</td>
                      <td className="px-3 py-3 text-slate-400 text-xs">{fmtDate(c.last_login)}</td>
                      <td className="px-3 py-3">
                        {c.is_blocked ? (
                          <Badge data-testid={`status-badge-${c.id}`} className="bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/10">Bloqueada</Badge>
                        ) : (
                          <Badge data-testid={`status-badge-${c.id}`} className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/10">Activa</Badge>
                        )}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <Button data-testid={`toggle-status-btn-${c.id}`} size="sm" variant="ghost" onClick={() => toggleStatus(c)}
                          className={c.is_blocked ? "text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10" : "text-red-400 hover:text-red-300 hover:bg-red-500/10"}>
                          {c.is_blocked ? (<><CheckCircle2 className="w-4 h-4 mr-1.5" /> Activar</>) : (<><Ban className="w-4 h-4 mr-1.5" /> Bloquear</>)}
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {!loading && companies.length === 0 && (
                    <tr><td colSpan={9} className="px-5 py-10 text-center text-slate-500">No hay empresas registradas</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "pagos" && (
          <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="payments-table">
                <thead>
                  <tr className="text-left text-xs text-slate-400 border-b border-slate-800">
                    <th className="px-5 py-3 font-medium">Cliente</th>
                    <th className="px-3 py-3 font-medium">Correo</th>
                    <th className="px-3 py-3 font-medium">Plan</th>
                    <th className="px-3 py-3 font-medium">Solicitud</th>
                    <th className="px-3 py-3 font-medium">Último pago</th>
                    <th className="px-3 py-3 font-medium">Pagado hasta</th>
                    <th className="px-3 py-3 font-medium">Estado</th>
                    <th className="px-5 py-3 font-medium text-right">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((c) => {
                    const st = PAY_STATUS[c.payment_status] || PAY_STATUS.free;
                    return (
                      <tr key={c.id} data-testid={`payment-row-${c.id}`} className="border-b border-slate-800/60 hover:bg-slate-800/30">
                        <td className="px-5 py-3">
                          <p className="font-medium text-white">{c.name || "—"}</p>
                          <p className="text-xs text-slate-500">{c.rut || "Sin RUT"}</p>
                        </td>
                        <td className="px-3 py-3 text-slate-300">{c.owner_email || "—"}</td>
                        <td className="px-3 py-3">
                          <span className={`text-xs border rounded-full px-2.5 py-1 capitalize ${PLAN_STYLES[c.plan] || PLAN_STYLES.free}`}>{c.plan}</span>
                        </td>
                        <td className="px-3 py-3">
                          {c.requested_plan ? (
                            <Badge className="bg-blue-500/10 text-blue-400 border border-blue-500/30 hover:bg-blue-500/10 capitalize">{c.requested_plan}</Badge>
                          ) : <span className="text-slate-600 text-xs">—</span>}
                        </td>
                        <td className="px-3 py-3 text-slate-400 text-xs">
                          {c.last_payment ? `${fmtDate(c.last_payment.paid_at)} · ${fmtCLP(c.last_payment.amount)}` : "—"}
                        </td>
                        <td className="px-3 py-3 text-slate-300 text-xs">{fmtDate(c.paid_until)}</td>
                        <td className="px-3 py-3">
                          <Badge data-testid={`pay-status-${c.id}`} className={`border hover:bg-transparent ${st.cls}`}>{st.label}</Badge>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <Button data-testid={`register-payment-btn-${c.id}`} size="sm" variant="ghost" onClick={() => setPayModal(c)}
                            className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10">
                            <CreditCard className="w-4 h-4 mr-1.5" /> Registrar pago
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                  {!loading && payments.length === 0 && (
                    <tr><td colSpan={8} className="px-5 py-10 text-center text-slate-500">Sin datos</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {payModal && (
        <PaymentModal company={payModal} token={token} onClose={() => setPayModal(null)}
          onDone={() => { setPayModal(null); load(token); }} />
      )}
    </div>
  );
}
